import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarBlank, CheckCircle, Trash, ArrowSquareOut, Warning } from "@phosphor-icons/react";

const STATUS_TONE = {
  pending:   "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  confirmed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  cancelled: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  completed: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300",
};

function fmt(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso || "—"; } }

export default function AdminStartPages() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/start-page-reservations?status=${status}`);
      setRows(data?.reservations || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [status]);

  const activate = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/admin/start-page-reservations/${r.id}/activate`);
      toast.success(`Activated ${r.date} for @${r.username}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete @${r.username}'s reservation for ${r.date}?`)) return;
    setBusy(r.id);
    try {
      await api.delete(`/admin/start-page-reservations/${r.id}`);
      toast.success("Reservation deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            Start Page reservations
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Users book a specific day; you activate their slot after the external payment lands.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { v: "pending", label: "Pending" },
            { v: "confirmed", label: "Active" },
            { v: "cancelled", label: "Cancelled" },
            { v: "all", label: "All" },
          ].map((t) => (
            <button
              key={t.v}
              data-testid={`sp-tab-${t.v}`}
              onClick={() => setStatus(t.v)}
              className={`rounded-full px-4 py-1.5 text-xs font-black uppercase tracking-widest border transition ${
                status === t.v
                  ? "bg-blue-700 text-white border-blue-800"
                  : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-blue-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left py-3 px-4">Date</th>
              <th className="text-left py-3 px-4">Website</th>
              <th className="text-left py-3 px-4">Owner</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-left py-3 px-4">Booked</th>
              <th className="text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="py-8 text-center text-slate-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-slate-500">
                  <div className="flex flex-col items-center gap-2">
                    <CalendarBlank size={20} weight="duotone" />
                    No reservations in this bucket.
                  </div>
                </td>
              </tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={`sp-row-${r.id}`} className="border-t border-slate-100 dark:border-slate-800">
                <td className="py-3 px-4 align-top font-semibold text-slate-900 dark:text-white inline-flex items-center gap-1">
                  <CalendarBlank size={14} weight="fill" className="text-blue-600" />
                  {r.date}
                </td>
                <td className="py-3 px-4 align-top">
                  <div className="font-semibold text-slate-900 dark:text-white truncate max-w-[280px]">{r.website_title || "(deleted)"}</div>
                  <a href={r.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:underline truncate max-w-[280px]">
                    {r.website_url} <ArrowSquareOut size={10} weight="bold" />
                  </a>
                </td>
                <td className="py-3 px-4 align-top text-slate-700 dark:text-slate-200">@{r.username || "?"}</td>
                <td className="py-3 px-4 align-top">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${STATUS_TONE[r.status] || "bg-slate-100"}`}>
                    {r.status}
                  </span>
                  {r.checkout_url && (
                    <div className="mt-1">
                      <a href={r.checkout_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:underline">
                        checkout link <ArrowSquareOut size={10} weight="bold" />
                      </a>
                    </div>
                  )}
                </td>
                <td className="py-3 px-4 align-top text-slate-500 dark:text-slate-400">{fmt(r.created_at)}</td>
                <td className="py-3 px-4 align-top">
                  <div className="flex items-center justify-end gap-2 flex-wrap">
                    {r.status === "pending" && (
                      <button
                        data-testid={`sp-activate-${r.id}`}
                        onClick={() => activate(r)}
                        disabled={busy === r.id}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 disabled:opacity-60"
                      >
                        <CheckCircle size={12} weight="fill" /> Activate
                      </button>
                    )}
                    <button
                      data-testid={`sp-delete-${r.id}`}
                      onClick={() => remove(r)}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold px-3 py-1.5 disabled:opacity-60"
                    >
                      <Trash size={12} /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <Warning size={12} weight="fill" className="text-amber-500" />
        Once activated, that user&apos;s website becomes the Start Page on the reserved date.
      </div>
    </AdminLayout>
  );
}
